import React, { useState } from "react";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";

import { gql } from "apollo-boost";
import IconButton from "@material-ui/core/IconButton";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import StatusBadge from "designV2/components/StatusBadge";
import DialogShell from "designV2/components/DialogShell";
import Button from "designV2/components/Button";
import PaymentDetails from "superAdminViews/finance/pay/PaymentDetails";
import "../superFinance.css";

const REFUND_TRANSACTION = gql`
  mutation refundTransactionByAdmin($_id: ID!) {
    refundTransactionByAdmin(_id: $_id) {
      _id
      status
    }
  }
`;

const STATUS_TONES = {
  PAID: "success",
  UNPAID: "warning",
  FAILED: "danger",
  FAILED_DUE_TO_REJECTION: "danger",
};

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const humanize = (value) => (value ? value.replace(/_/g, " ").toLowerCase() : "");

const formatDateTime = (value) => (value ? moment(value).format("MMM D, YYYY [at] hh:mm A") : "-");

const CopyableField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="superTransactionField">
      <span className="superTransactionLabel">{label}</span>
      <span className="superTransactionValueRow">
        <span className="superTransactionMono">{value}</span>
        <IconButton
          size="small"
          className="superTransactionCopyButton"
          onClick={handleCopy}
          title="Copy"
        >
          {copied ? <CheckIcon fontSize="small" /> : <FileCopyIcon style={{ fontSize: 16 }} />}
        </IconButton>
      </span>
    </div>
  );
};

const Field = ({ label, value }) =>
  value ? (
    <div className="superTransactionField">
      <span className="superTransactionLabel">{label}</span>
      <span className="superTransactionValue">{value}</span>
    </div>
  ) : null;

/**
 * TransactionDetails - content-only, rendered inside a shared
 * DialogShell by every call site (Payments, Transactions), the same as
 * PaymentDetails and OrderDetails. Prop contract is unchanged —
 * { transaction, close, refetch, hasRefunder } — so the existing call
 * sites keep working; `hasPaymentsDetails` was accepted but never read
 * and has been dropped.
 *
 * Only the super admin ever sees this; the shop admin has its own
 * ShopTransactionDetails without the refund path.
 */
const TransactionDetails = ({ transaction, close, refetch, hasRefunder }) => {
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [error, setError] = useState("");

  const [refundTransactionByAdmin, { loading: refunding }] = useMutation(REFUND_TRANSACTION);

  const refund = () => {
    setError("");
    refundTransactionByAdmin({ variables: { _id: transaction._id } })
      .then(() => {
        setRefundOpen(false);
        if (refetch) refetch();
        if (close) close();
      })
      .catch((err) => {
        setRefundOpen(false);
        setError(readableError(err));
      });
  };

  // The counterparty is whichever of the three is set; the old layout
  // had a labelled row for each and left two of them blank every time.
  const party = transaction.shop
    ? { role: "Shop", name: transaction.shop.name, id: transaction.shop._id }
    : transaction.driver
    ? { role: "Driver", name: transaction.driver.fullName, id: transaction.driver._id }
    : transaction.user
    ? { role: "Customer", name: transaction.user.fullName, id: transaction.user._id }
    : null;

  const payments = transaction.payments || [];
  const canRefund = hasRefunder && transaction.status === "PAID" && !transaction.refundId;

  return (
    <div className="superTransactionBody">
      <div className="superTransactionTopRow">
        <StatusBadge
          status={STATUS_TONES[transaction.status] || "neutral"}
          label={humanize(transaction.status) || "Unknown"}
        />
        {transaction.reversed && <StatusBadge status="danger" label="Reversed" dot={false} />}
        {transaction.refundId && <StatusBadge status="warning" label="Refunded" dot={false} />}
        <span className="superTransactionAmount">
          ${Number(transaction.amount || 0).toFixed(2)}
        </span>
      </div>

      {error && (
        <div className="dsFormMessage error" role="alert">
          {error}
        </div>
      )}

      <div className="superTransactionGrid">
        <CopyableField label="Transaction ref" value={transaction.transactionId} />
        <Field label="Type" value={humanize(transaction.type)} />
        {party && (
          <React.Fragment>
            <Field label={party.role} value={party.name || "-"} />
            <CopyableField label={`${party.role} ID`} value={party.id} />
          </React.Fragment>
        )}
        <Field label="Paid at" value={transaction.paidAt ? formatDateTime(transaction.paidAt) : null} />
        <Field label="Created" value={formatDateTime(transaction.createdAt)} />
        <Field label="Updated" value={formatDateTime(transaction.updatedAt)} />
        <CopyableField label="Payment intent" value={transaction.paymentIntent} />
        <CopyableField label="Reversal ID" value={transaction.reversalId} />
        <CopyableField label="Refund ID" value={transaction.refundId} />
      </div>

      {payments.length > 0 && (
        <div className="superTransactionField">
          <span className="superTransactionLabel">Payments ({payments.length})</span>
          <div className="superTransactionPayments">
            {payments.map((payment) => (
              <button
                type="button"
                key={payment._id}
                className="superTransactionPaymentRow"
                onClick={() => setPaymentDetail(payment)}
              >
                <span className="superTransactionPaymentText">
                  <span className="superTransactionPaymentId">{payment._id}</span>
                  <span className="superTransactionPaymentMeta">
                    {payment.createdAt ? moment(payment.createdAt).format("MMM D, YYYY") : ""}
                    {payment.for ? ` · ${humanize(payment.for)}` : ""}
                  </span>
                </span>
                <span className="superTransactionPaymentAmount">
                  ${Number(payment.amount || 0).toFixed(2)}
                </span>
                <ChevronRightIcon className="superTransactionPaymentChevron" fontSize="small" />
              </button>
            ))}
          </div>
        </div>
      )}

      {canRefund && (
        <div className="superTransactionFooter">
          <Button variant="danger" onClick={() => setRefundOpen(true)}>
            Refund transaction
          </Button>
        </div>
      )}

      <DialogShell
        open={Boolean(paymentDetail)}
        onClose={() => setPaymentDetail(null)}
        title="Payment details"
      >
        {paymentDetail && (
          <PaymentDetails close={() => setPaymentDetail(null)} payment={paymentDetail} />
        )}
      </DialogShell>

      <DialogShell
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Refund transaction"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setRefundOpen(false), disabled: refunding },
          { label: "Refund", onClick: refund, variant: "danger", loading: refunding },
        ]}
      >
        <div className="superPayoutIntro">
          ${Number(transaction.amount || 0).toFixed(2)} will be refunded
          {party && party.name ? ` to ${party.name}` : ""}. This cannot be undone.
        </div>
      </DialogShell>
    </div>
  );
};

export default TransactionDetails;
